<template>
  <v-list-item draggable
    :class="['file', 'it-hv', 'py-0', { 'blue': isSelecting, 'lighten-4': isSelecting }, printExt]"
    @dragstart="dragstart"
    @dragend="dragend"
    @mousedown="select"
    @click.stop="select"
    :disabled="!isActive || isLoading">
    <v-list-item-avatar class="my-0">
      <v-icon small :class="printColor">{{ printIcon }}</v-icon>
    </v-list-item-avatar>
    <v-list-item-content v-if="$vuetify.breakpoint.smAndUp">
      <v-row dense class="transparent">
        <v-col cols="8" class="pa-0 text-truncate">
          <button type="button" class="flex name-link text-truncate body-2 pl-1" @click.stop="open">{{ printName }}</button>
          <div class="px-1 overline">
            <span v-if="isLocked" class="mr-2 overline">
              <v-icon x-small>fa-lock</v-icon>&nbsp;{{ printAuthor }}
            </span>
            <span v-if="isSharedDirectory || isShared" class="mr-2 overline">
              <v-icon x-small>fa-link</v-icon>&nbsp;共有中
            </span>
          </div>
        </v-col>
        <v-col cols="2" align-self="center" class="caption text-right grey--text text--darken-2 py-0 px-2 text-truncate">{{ printModifiedAt }}</v-col>
        <v-col cols="2" align-self="center" class="caption text-right grey--text text--darken-2 py-0 px-2 text-truncate">{{ printSize }}</v-col>
      </v-row>
    </v-list-item-content>
    <v-list-item-content v-else>
      <v-list-item-title>
        <button type="button" class="flex name-link text-truncate body-2" @click.stop="open">{{ printName }}</button>
      </v-list-item-title>
      <v-list-item-subtitle class="caption">{{ printModifiedAt }} {{ printSize }}</v-list-item-subtitle>
    </v-list-item-content>
    <v-list-item-action>
      <v-menu bottom left offset-y :close-on-click="true">
        <template v-slot:activator="{ on }">
          <v-btn icon v-on="on" :loading="isLoading">
            <v-icon small color="secondary">fa-ellipsis-v</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-if="isAuthed && !isTrashed" @click="rename">
            <v-list-item-icon><v-icon small>fal fa-pencil</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>名前の変更</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="download">
            <v-list-item-icon><v-icon small>fal fa-download</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>ダウンロード</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="isAuthed && !isTrashed" @click="$refs.replacer.click()">
            <v-list-item-icon><v-icon small>fal fa-upload</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>新しいバージョンをアップロード</v-list-item-title>
              <input type="file" ref="replacer"
                style="display:none;"
                @change="replace($event.target.files)"
              />
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="!isTrashed && !isSharedDirectory && isShared" @click="unshare">
            <v-list-item-icon><v-icon small>fal fa-user-minus</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>共有を止める</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-else-if="!isTrashed && !isSharedDirectory" @click="share">
            <v-list-item-icon><v-icon small>fal fa-user-plus</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>共有する</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="isAuthed && isSetClientId" @click="trash">
            <v-list-item-icon><v-icon small>{{ isTrashed ? 'fal fa-books' : 'fal fa-trash-alt' }}</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ isTrashed ? 'メディア' : 'ごみ箱' }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
const DEFAULT = {
  STATUS: {
    LOADING: false,
    SELECTING: false,
    DRAGGING: false
  }
}
const FILES = {
  FILE: {
    icon: 'fa-file',
    color: 'gray--text text--darken-2'
  },
  DOC: {
    icon: 'fa-file-word',
    color: 'blue--text text--darken-3'
  },
  DOCX: {
    icon: 'fa-file-word',
    color: 'blue--text text--darken-3'
  },
  XLS: {
    icon: 'fa-file-excel',
    color: 'green--text text--darken-3'
  },
  XLSX: {
    icon: 'fa-file-excel',
    color: 'green--text text--darken-3'
  },
  PPT: {
    icon: 'fa-file-powerpoint',
    color: 'orange--text text--darken-3'
  },
  PPTX: {
    icon: 'fa-file-powerpoint',
    color: 'orange--text text--darken-3'
  },
  PDF: {
    icon: 'fa-file-pdf',
    color: 'red--text text--darken-3'
  },
  PNG: {
    icon: 'fa-file-image',
    color: 'grey--text text--darken-4'
  },
  JPG: {
    icon: 'fa-file-image',
    color: 'grey--text text--darken-4'
  },
  JPEG: {
    icon: 'fa-file-image',
    color: 'grey--text text--darken-4'
  },
  BMP: {
    icon: 'fa-file-image',
    color: 'grey--text text--darken-4'
  },
  GIF: {
    icon: 'fa-file-image',
    color: 'grey--text text--darken-4'
  },
  TXT: {
    icon: 'fa-file-alt',
    color: 'blue-grey--text text--darken-1'
  },
  MD: {
    icon: 'fa-file-invoice',
    color: 'blue-grey--text text--darken-1'
  },
  ZIP: {
    icon: 'fa-file-archive',
    color: 'brown--text'
  },
  EML: {
    icon: 'fa-envelope',
    color: 'blue--text text--darken-1'
  }
}
export default {
  name: 'filer-file',
  props: [
    'cid',
    'authed',
    'shared',
    'trashed',
    'status',
    'item'
  ],
  components: {
  },
  data () {
    return {
      config: {
        status: {
          loading: DEFAULT.STATUS.LOADING,
          selecting: DEFAULT.STATUS.SELECTING,
          dragging: DEFAULT.STATUS.DRAGGING
        }
      },
      meta: {
        icons: {
          valid: Object.keys(FILES)
        },
        size: {
          unit: ['B', 'B', 'B', 'KB', 'KB', 'KB', 'MB', 'MB', 'MB', 'GB', 'GB', 'GB'],
          offset: [1, 2, 3, 1, 2, 3, 1, 2, 3, 1, 2, 3]
        }
      }
    }
  },
  methods: {
    state: function (req) {
      const { type, status } = req
      if (type && status != null) {
        this.$set(this.config.status, req.type, req.status)
      }
    },
    open: function () {
      const { item } = this.$props
      if (item) {
        this.$emit('emit', {
          action: 'open',
          data: item
        })
      }
    },
    rename: function () {
      const { item } = this.$props
      if (item) {
        this.$emit('emit', {
          action: 'rename',
          data: item
        })
      }
    },
    trash: function () {
      if (this.$props.item && this.$props.status) {
        this.$set(this.config.status, 'loading', true)
        this.$emit('emit', {
          action: this.$props.status.permits.trash ? 'trash' : 'restore',
          data: this.$props.item
        })
      }
    },
    replace: function (files) {
      const { item } = this.$props
      if (item && files && files.length) {
        this.$emit('emit', {
          action: 'replace',
          data: {
            dest: item,
            file: files[0]
          }
        })
      }
    },
    share: function () {
      const { item, status } = this.$props
      if (item && status) {
      console.log(this.$props)
        this.$emit('emit', {
          action: 'share',
          data: item
        })
      }
    },
    unshare: function () {
      const { item, status } = this.$props
      if (item && status) {
        this.$emit('emit', {
          action: 'unshare',
          data: item
        })
      }
    },
    select: function (e) {
      if (
        this.$props.item &&
        e &&
        e.type &&
        this.$props.status &&
        this.$props.status.permits &&
        this.$props.status.permits.select
      ) {
        if (e.type === 'mousedown') {
          if (e.metaKey) {
            this.state({
              type: 'selecting',
              status: !this.config.status.selecting
            })
            this.$emit('emit', {
              action: 'select',
              data: {
                item: this.$props.item,
                event: e
              }
            })
          } else if (!this.config.status.selecting) {
            this.state({
              type: 'selecting',
              status: true
            })
            this.$emit('emit', {
              action: 'select',
              data: {
                item: this.$props.item,
                event: e
              }
            })
          }
        } else if (e.type === 'click') {
          if (!this.config.status.dragging) {
            this.state({
              type: 'selecting',
              status: true
            })
            this.$emit('emit', {
              action: 'select',
              data: {
                item: this.$props.item,
                event: e
              }
            })
          }
        }
      }
    },
    dragstart: function (e) {
      this.$set(this.config.status, 'dragging', true)
      this.$emit('emit', {
        action: 'dragstart',
        data: e
      })
    },
    dragend: function (e) {
      this.$set(this.config.status, 'dragging', false)
    },
    download: function () {
      if (this.$props.item && this.$props.item.id) {
        this.$set(this.config.status, 'loading', true)
        let uid = this.$store.getters['author/get']('tokens').box
        let url = this.$url.api + 'box/download/' + this.$props.item.id
        this.$firebase.auth().currentUser.getIdToken(true).then(token => {
          this.axios.post(url, {
            uid: uid
          }, {
            headers: {
              'firebase-token': token
            }
          }).then(res => {
            console.log(res)
            this.$set(this.config.status, 'loading', false)
            if (res.data && res.data.success) {
              window.location.href = res.data.data
            } else if (
              res.data &&
              res.data.data &&
              res.data.data.response &&
              res.data.data.response.body &&
              res.data.data.response.body.code
            ) {
              let code = res.data.data.response.body.code
              let err = this.$error.box.file.open[code] ? this.$error.box.file.open[code] : ''
              if (err) {
                this.$store.dispatch('notifier/addAlert', {
                  title: 'ファイルのダウンロード',
                  message: err
                })
              }
            }
          })
        })
      }
    },
    emitted: function (req) {
      if (
        req &&
        req.action &&
        typeof this[req.action] === 'function'
      ) {
        this[req.action](req.data)
      }
    }
  },
  computed: {
    isLoading () {
      return this.config.status.loading
    },
    isActive () {
      return !!(
        this.$props.item &&
        this.$props.item.active
      )
    },
    isLocked () {
      return !!(
        this.$props.item &&
        this.$props.tags &&
        this.$props.tags.authed
      )
    },
    isSetClientId () {
      return !!this.$props.cid
    },
    isAuthed () {
      return this.$props.authed && !(
        this.$props.item.tags &&
        this.$props.item.tags.authed &&
        this.$props.item.tags.authed !== this.$store.getters['author/get']('userid')
      )
    },
    isShared () {
      return !!(
        this.$props.item &&
        this.$props.item.tags &&
        this.$props.item.tags.shared
      )
    },
    isSharedDirectory () {
      return this.$props.shared
    },
    isTrashed () {
      return this.$props.trashed
    },
    isItemLoading () {
      return (
        this.$props.item &&
        this.$props.item.status &&
        this.$props.item.status.loading
      )
    },
    isCoreLoading () {
      return this.$props.status && this.$props.status.loading
    },
    isSelecting () {
      return this.config.status.selecting
    },
    isInserted () {
      return (
        this.$props.item &&
        this.$props.item.status &&
        this.$props.item.status.inserting
      )
    },
    printIcon () {
      if (this.$props.item && this.$props.item.name) {
        let ext = this.$props.item.name.split('.').pop()
        return (ext !== this.$props.item.name && this.meta.icons.valid.includes(ext.toUpperCase())) ? ['fas', FILES[ext.toUpperCase()].icon].join(' ') : ['fas', FILES.FILE.icon].join(' ')
      } else {
        return FILES.FILE.icon
      }
    },
    printColor () {
      if (this.$props.item && this.$props.item.name) {
        let ext = this.$props.item.name.split('.').pop()
        return (ext !== this.$props.item.name && this.meta.icons.valid.includes(ext.toUpperCase())) ? FILES[ext.toUpperCase()].color : FILES.FILE.color
      } else {
        return FILES.FILE.color
      }
    },
    printExt () {
      if (this.$props.item && this.$props.item.name) {
        let ext = this.$props.item.name.split('.').pop()
        return (ext !== this.$props.item.name) ? ext.toLowerCase() : ''
      } else {
        return ''
      }
    },
    printAuthor () {
      return (
        this.$props.item &&
        this.$props.item.tags &&
        this.$props.item.tags.authed
      ) ? this.$props.item.tags.authed : ''
    },
    printName () {
      if (this.$props.item && this.$props.item.name) {
        if (this.$props.trashed) {
          let nameInfo = this.$props.item.name.split('_')
          let date = nameInfo.shift()
          return nameInfo.join('_')
        } else {
          return this.$props.item.name
        }
      } else {
        return ''
      }
    },
    printModifiedAt () {
      if (this.$props.item && this.$props.item.modified_at) {
        if (this.$props.trashed) {
          let nameInfo = this.$props.item.name.split('_')
          let date = nameInfo.shift()
          return `${this.$moment(date).format('YYYY年MM月DD日')}（削除）`
        } else {
          return this.$moment(this.$props.item.modified_at).format('YYYY年MM月DD日')
        }
      } else {
        return ''
      }
    },
    printSize () {
      if (
        this.$props.item &&
        this.$props.item.size
      ) {
        let size = '' + this.$props.item.size
        let unit = this.meta.size.unit[size.length - 1] ? this.meta.size.unit[size.length - 1] : 'B'
        let rounded = this.meta.size.offset[size.length - 1] ? size.substr(0, this.meta.size.offset[size.length - 1]) : size
        return rounded + ' ' + unit
      } else {
        return ''
      }
    },
    getItem () {
      return this.$props.item
    }
  }
}
</script>

<style lang="scss">
.filer {
  .file {
    .name-link {
      &:hover {
        text-decoration: underline !important;
      }
    }
  }
}
</style>
